import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Loader2, Trash2, AlertTriangle, BookOpen, Users, FileText } from "lucide-react";
import checkPermission from "../../utils/permission";
import { ApiService } from "../../services/api";

interface LessonInfo {
  id: string;
  title: string;
  description: string;
  sectionCount?: number;
  contentCount?: number;
  studentCount?: number;
  responsibleUsers?: Array<any>;
  isActive: boolean;
  isCertificate: boolean;
  coverMedia?: {
    url: string;
  };
  createdAt: string;
  updatedAt: string;
}

export default function DeleteLessonPage() {
  const navigate = useNavigate();
  const { lessonId } = useParams<{ lessonId: string }>();
  const hasDeletePermission = checkPermission("lesson_delete");

  useEffect(() => {
    if (!hasDeletePermission) {
      navigate("/lessons");
    }
  }, [hasDeletePermission, navigate]);

  const [lesson, setLesson] = useState<LessonInfo | null>(null);
  const [sectionCount, setSectionCount] = useState<number>(0);
  const [contentCount, setContentCount] = useState<number>(0);
  const [studentCount, setStudentCount] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState("");
  const [confirmText, setConfirmText] = useState("");

  useEffect(() => {
    const load = async () => {
      if (!lessonId) return;
      try {
        setLoading(true);
        setError("");

        const [lessonData, sectionsData, responsibleData] = await Promise.all([
          ApiService.getLesson(lessonId),
          ApiService.getLessonSections(lessonId).catch(() => []),
          ApiService.getLessonResponsible(lessonId).catch(() => []),
        ]);

        setLesson(lessonData);

        const sections = Array.isArray(sectionsData) ? sectionsData : sectionsData?.data || [];
        setSectionCount(sections.length);
        const sectionsWithContents = await Promise.all(
          sections.map(async (section: any) => {
            try {
              const contents = await ApiService.getSectionContents(lessonId, section.id);
              return { ...section, contents };
            } catch (err) {
              return { ...section, contents: [] };
            }
          }),
        );

        let totalContents = 0;
        sectionsWithContents.forEach((section: any) => {
          if (section.contents && Array.isArray(section.contents)) {
            totalContents += section.contents.length;
          }
        });
        setContentCount(totalContents);

        const responsibleUsers = Array.isArray(responsibleData) ? responsibleData : responsibleData?.data || [];
        setStudentCount(responsibleUsers.length);
      } catch (err: any) {
        setError(err.message || "Ders bilgileri yüklenemedi");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [lessonId]);

  const handleDelete = async () => {
    if (!lessonId || !lesson) return;
    if (confirmText !== lesson.title) {
      setError("Ders adını doğru yazmadınız!");
      return;
    }
    try {
      setDeleting(true);
      setError("");
      await ApiService.deleteLesson(lessonId);
      navigate("/lessons");
    } catch (err: any) {
      setError(err.message || "Ders silinemedi");
    } finally {
      setDeleting(false);
    }
  };

  if (!hasDeletePermission) {
    return null;
  }

  const hasSections = sectionCount > 0;
  const hasContents = contentCount > 0;

  return (
    <div className="h-[88vh] bg-gray-50 overflow-hidden flex flex-col">
      <nav className="bg-white border-b border-gray-200 shadow-sm flex-shrink-0">
        <div className="px-6 py-3">
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-gray-900">E-Eğitim</span>
            </div>
            <div className="flex items-center gap-6 text-sm text-gray-600">
              <button
                onClick={() => navigate("/lessons")}
                className={`pb-3 transition-all duration-200 text-blue-600 font-medium border-b-2 border-blue-500`}>
                Dersler
              </button>
              <button onClick={() => navigate("/lessons?tab=homeworks")} className={`pb-3 transition-all duration-200 hover:text-gray-900`}>
                Ödevler
              </button>
              <button
                onClick={() => navigate("/lessons?tab=deficiencies")}
                className={`pb-3 transition-all duration-200 hover:text-gray-900`}>
                Eksikler
              </button>
              <button onClick={() => navigate("/lessons?tab=reports")} className={`pb-3 transition-all duration-200 hover:text-gray-900`}>
                Raporlar
              </button>
            </div>
          </div>
        </div>
      </nav>

      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-5xl mx-auto">
          <div className="bg-gradient-to-r from-red-600 to-red-700 rounded-xl p-6 mb-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold">Dersi Sil</h1>
                <p className="text-red-100 text-sm mt-1">Bu işlem geri alınamaz. Lütfen dikkatli olun.</p>
              </div>
              {lesson && (
                <div className="text-right">
                  <p className="text-white font-medium text-lg">{lesson.title}</p>
                  <p className="text-red-100 text-sm">Silinecek Ders</p>
                </div>
              )}
            </div>
          </div>

          {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm p-3 rounded-lg mb-6">{error}</div>}

          {loading ? (
            <div className="flex justify-center items-center py-16">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
            </div>
          ) : lesson ? (
            <div className="space-y-6">
              <div className="bg-red-50 border border-red-200 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <AlertTriangle className="w-6 h-6 text-red-600" />
                  <h3 className="text-red-800 font-semibold">Dikkat!</h3>
                </div>
                <p className="text-red-700 text-sm mb-3">
                  Bu işlem geri alınamaz. Dersi sildikten sonra aşağıdaki veriler kalıcı olarak kaybolacaktır:
                </p>
                <ul className="space-y-3 text-red-700 text-sm">
                  <li className="flex items-center gap-3">
                    <span className="w-2 h-2 bg-red-500 rounded-full flex-shrink-0"></span>
                    <span className="font-medium">Ders Bilgileri ve Ayarları</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <span className="w-2 h-2 bg-red-500 rounded-full flex-shrink-0"></span>
                    <span className="font-medium">Tüm Ders Bölümleri ve İçerikleri</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <span className="w-2 h-2 bg-red-500 rounded-full flex-shrink-0"></span>
                    <span className="font-medium">Öğrenci Kayıtları ve İlerlemeleri</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <span className="w-2 h-2 bg-red-500 rounded-full flex-shrink-0"></span>
                    <span className="font-medium">Akademik Takvim ve Ödev Kayıtları</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <span className="w-2 h-2 bg-red-500 rounded-full flex-shrink-0"></span>
                    <span className="font-medium">Yorumlar, Notlar ve Değerlendirmeler</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white border border-gray-200 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <BookOpen className="w-5 h-5 text-red-600" />
                  <h3 className="text-gray-900 font-medium">Ders Bilgileri</h3>
                </div>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-gray-500 text-xs mb-1">Ders Adı</label>
                      <p className="text-gray-900 font-medium">{lesson.title}</p>
                    </div>
                    {lesson.description && (
                      <div>
                        <label className="block text-gray-500 text-xs mb-1">Açıklama</label>
                        <p className="text-gray-700 text-sm">{lesson.description}</p>
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                      <div className="flex items-center gap-2 mb-2">
                        <FileText className="w-4 h-4 text-red-600" />
                        <span className="text-red-700 text-sm font-medium">Bölüm Sayısı</span>
                      </div>
                      <p className={`text-2xl font-bold ${hasSections ? "text-red-600" : "text-red-400"}`}>{sectionCount}</p>
                    </div>

                    <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                      <div className="flex items-center gap-2 mb-2">
                        <BookOpen className="w-4 h-4 text-red-600" />
                        <span className="text-red-700 text-sm font-medium">İçerik Sayısı</span>
                      </div>
                      <p className={`text-2xl font-bold ${hasContents ? "text-red-600" : "text-red-400"}`}>{contentCount}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-gray-500 text-xs mb-1">Durum</label>
                      <p className="text-gray-900 font-medium">{lesson.isActive ? "Aktif" : "Pasif"}</p>
                    </div>
                    <div>
                      <label className="block text-gray-500 text-xs mb-1">Sertifika</label>
                      <p className="text-gray-900 font-medium">{lesson.isCertificate ? "Var" : "Yok"}</p>
                    </div>
                    <div>
                      <label className="block text-gray-500 text-xs mb-1">Oluşturma Tarihi</label>
                      <p className="text-gray-700 text-sm">{new Date(lesson.createdAt).toLocaleDateString("tr-TR")}</p>
                    </div>
                    <div>
                      <label className="block text-gray-500 text-xs mb-1">Güncelleme Tarihi</label>
                      <p className="text-gray-700 text-sm">{new Date(lesson.updatedAt).toLocaleDateString("tr-TR")}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white border border-gray-200 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Trash2 className="w-5 h-5 text-red-600" />
                  <h4 className="text-gray-900 font-medium">Silme İşlemini Onayla</h4>
                </div>
                <p className="text-gray-600 text-sm mb-4">Silme işlemini onaylamak için ders adını tam olarak yazın:</p>
                <div className="mb-4">
                  <p className="text-red-700 font-mono text-sm mb-2 bg-red-50 p-2 rounded border">{lesson.title}</p>
                  <input
                    type="text"
                    value={confirmText}
                    onChange={(e) => setConfirmText(e.target.value)}
                    placeholder="Ders adını buraya yazın..."
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-400 focus:border-red-400"
                  />
                </div>
                {confirmText && confirmText !== lesson.title && (
                  <div className="flex items-center gap-2 text-red-600 text-xs bg-red-50 p-3 rounded-lg border border-red-200">
                    <span className="text-red-500 text-sm">❌</span>
                    <span className="font-medium">Girilen metin ders adı ile eşleşmiyor.</span>
                  </div>
                )}
                {confirmText === lesson.title && (
                  <div className="flex items-center gap-2 text-green-600 text-xs bg-green-50 p-3 rounded-lg border border-green-200">
                    <span className="text-green-500 text-sm">✅</span>
                    <span className="font-medium">Ders adı doğru şekilde girildi.</span>
                  </div>
                )}
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => navigate(-1)}
                  disabled={deleting}
                  className="flex-1 px-4 py-3 bg-white text-gray-700 rounded-xl border hover:bg-gray-50 text-sm font-medium disabled:opacity-50">
                  İptal
                </button>
                <button
                  onClick={handleDelete}
                  disabled={confirmText !== lesson.title || deleting || loading}
                  className="flex-1 px-4 py-3 bg-red-600 text-white rounded-xl hover:bg-red-700 disabled:opacity-60 text-sm font-medium flex items-center justify-center gap-2">
                  {deleting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Siliniyor...
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4" />
                      Dersi Sil
                    </>
                  )}
                </button>
              </div>
            </div>
          ) : (
            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <div className="text-center py-16 text-gray-500">
                <BookOpen className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p className="text-sm">Ders bilgileri yüklenemedi</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
